<template>
  <v-treeview
    v-if="$store.state.devices.showTree"
    ref="topology_tree"
    disable-resize-watcher="true"
    style="cursor: pointer; width: 100%"
    :items="getTree"
    :active.sync="treeActive"
    :open.sync="$store.state.devices.treeOpened"
    :load-children="loadChildren"
    loading-icon="autorenew"
    expand-icon="expand_more"
    active-class="active"
    class="caption font-weight-medium font-condensend"
    item-disabled="disabled"
    activatable
    item-key="$id"
    return-object
  >

    <template v-slot:label="{ item, open, active }" :data-testid="$options.name + '__template__' + item.$id">
      <v-tooltip color="back" top :nudge-bottom="10" :nudge-left="1500" close-delay="0">
          <v-layout slot="activator" @contextmenu.prevent="contextHandler" fill-height align-center class="treeLeaf" px-0>
            <svg-icon
              :color="$getColor($store.state.monitoring.status[item.$id], $options.name)"
              name="circle"
              width="14"
            />
            <div style="width: 10px"></div>
            <svg-icon
              color="blackLine"
              :name="$checkItem(item)"
              width="28"
            />
            <span class="body-1 font-weight-medium font-condensend px-2">{{
              item.name
            }}</span>
          </v-layout>
        <span class="blacktext--text">{{$t(item.name)}}</span>
      </v-tooltip>
    </template>
  </v-treeview>
</template>

<script lang="ts">
import Vue from 'vue'
import svgIcon from '@/nms/components/media/svg/static/svg-icon.vue'
import { CCMDBObject } from '@entcor/ebridge'

// Типы для элементов дерева
interface TreeNode {
  $id: string
  cls: string
  children?: TreeNode[]
  $orderIndex?: number
  tree_view?: boolean
  type?: string
  [key: string]: any
}

interface ExceptionsMap {
  [key: string]: string[]
}

interface StoreState {
  devices: {
    tree: TreeNode[]
    treeOpened: TreeNode[]
    treeActive: TreeNode[]
    onceTreeOpened: string[]
  }
}

export default Vue.extend({
  name: 'TOPOLOGY_TREE',

  components: {
    svgIcon,
  },

  props: {
    opened: {
      type: Array as () => TreeNode[],
      default: () => []
    },
    hideClass: {
      type: Array as () => string[],
      default: () => []
    },

    treeShow: {
      type: String,
      default: () => 'physical'
    },
  },

  data(): {
    currentActive: TreeNode | null
    includes: string[]
    exceptions: ExceptionsMap
  } {
    return {
      currentActive: null,
      includes: ['service', 'location', 'cabinet', 'device'],
      exceptions: {
        device: ['tree_view'],
        module: ['tree_view'],
        location: ['type'],
      }
    }
  },

  watch: {

    'treeShow': {
      handler(newVal: any): void {
        console.log('treeShow', newVal)
      },
      deep: true
    },

    '$store.state.devices.tree': {
      handler(newVal: TreeNode[]): void {
        if (!newVal?.length) return
        this.setup()
      },
      deep: true
    },

    '$store.state.devices.treeOpened': {
      handler(newVal: TreeNode[]): void {
        if (newVal?.length) {
          newVal.forEach(val => {
            const onceOpened = this.$store.state.devices.onceTreeOpened as string[]
            if (!onceOpened.includes(val.$id)) {
              onceOpened.push(val.$id)
            }
          })
        }
      },
      deep: true
    },
  },

  computed: {
    getTree(): TreeNode[] {
      const filter = (
        arr: TreeNode[] | undefined, 
        inc: string[], 
        exc: ExceptionsMap, 
        parentCls?: string
      ): TreeNode[] => {
        return (arr || [])
          .map((n$, i) => {
            const n = new CCMDBObject({ ...n$ })
            if (n.children?.length !== n$.children?.length) {
              this.$set(n, 'children', n$.children)
            }
            n.$orderIndex = i
            
            if (!n.children?.length) {
              return n
            } else {
              n.children = filter(n.children, inc, exc, n.cls)
              return n
            }
          })
          .filter(n => {
            if (n.cls && inc.includes(n.cls) && !this.hideClass.includes(n.cls)) {
              if (Object.keys(exc).includes(n.cls)) {
                return !exc[n.cls].find(field => !n[field])
              }
              return true
            }
            return false
          })
      }
      
      const tree$ = filter(this.$store.state.devices.tree, this.includes, this.exceptions)
      return tree$
    },

    treeOpened: {
      get(): TreeNode[] {
        return this.$store.state.devices.treeOpened
      },
      set(newVal: TreeNode[]): void {
        if (!newVal) return
        
        if (newVal.length === 0 && this.$store.state.devices.treeOpened.length) {
          if (this.$store.state.devices.treeOpened.length === 1) {
            this.$set(this.$store.state.devices, 'treeOpened', [])
          } else {
            this.$store.state.devices.treeOpened.forEach((item: TreeNode, i: number) => {
              this.$set(this.$store.state.devices.treeOpened, i, item)
            })
            return
          }
        }
        this.$set(this.$store.state.devices, 'treeOpened', newVal)
      },
    },

    treeActive: {
      get(): TreeNode[] {
        return this.$store.state.devices.treeActive
      },
      set(newVal: TreeNode[]): void {
        if (!newVal?.length) {
          if (this.getCurrentTreeActive?.$id) {
            this.$nextTick(() => {
              this.$set(this.$store.state.devices, 'treeActive', [this.getCurrentTreeActive])
            })
          }
          return
        }
        
        if (Array.isArray(newVal) && newVal.length) {
          if (newVal[0].$id === this.getCurrentTreeActive?.$id) {
            return
          }
          if (!this.currentActive) {
            this.$store.dispatch('currentActiveTree', newVal[0])
            // this.currentActive = newVal[0]
          }
        }
        
        this.$set(this.$store.state.devices, 'treeActive', newVal)
        this.$store.dispatch('currentActiveTree', newVal[0])
        // this.currentActive = newVal[0]
      },
    },

    getCurrentTreeActive(): any {
      return this.$store.getters.getCurrentTreeActive
    },
  },

  async created(): Promise<void> {
    await this.$store.dispatch('loadIcons')
  },

  methods: {
    setup(): void {
      this.statusSubscribe('physical_tree')
    },

    async loadChildren(item: TreeNode): Promise<void> {
      await this.$store.dispatch('loadChildren', item)
    },

    contextHandler(e: Event): void {
      console.log('contextHandler', e)
    },

    testMethod(item: TreeNode): void {},

    async statusSubscribe(group: string): Promise<void> {
      this.statusUnsubscribe('physical_tree')
      this.$store.dispatch('measuresSubscribeInit', {
        name: 'addStatus',
        group: group,
        listener: 'status'
      })
    },

    subscribeHandler(e: { $id?: string; state?: any }): void {
      if (e.$id) {
        this.$store.dispatch('addStatus', e)
      }
    },

    statusUnsubscribe(group: string): void {
      this.$store.dispatch('measuresSubscribeClose', {
        handler: this.subscribeHandler,
        group: group,
        listener: 'status'
      })
    },
  },
})
</script>

<style lang="css" scoped>
.activeElement{
  border-radius: 14px;
}

.treeLeaf{
  height: 38px;
}

</style>